import React, { Suspense, useContext } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Navigate, RouteProps, useLocation } from 'react-router';
import {
  limitedUserHasAccess,
  Role,
  userHasAccess,
  userHasFullFeatureAccess,
} from 'models/Role';
import { Typography } from '@material-ui/core';
import MainLayout from 'layouts/MainLayout/MainLayout';
import MinimalLayout from 'layouts/MinimalLayout/MinimalLayout';
import Dashboard from 'views/Dashboard/Dashboard';
import LoginPage from 'views/Auth/LoginPage';
import PageLoader from 'components/Spinners/PageLoader';
import Alert from '@material-ui/lab/Alert';
import UserContext from 'state/UserContext';
import RateCard from 'components/RateCard/RateCard';
import RegisterPageWrapperWithCaptcha from 'views/Auth/RegisterPageWrapperWithCaptcha';
import VerifyEmailScreen from 'views/Auth/VerifyEmailScreen';
import EmailVerificationStatus from 'views/Auth/EmailVerificationStatus';
import { FeatureFlag } from 'api/organizationApi';

const AdminPage = React.lazy(() => import('views/Administration/AdminPage'));
const JobsListPage = React.lazy(() => import('views/Jobs/JobListPage'));
const JobPage = React.lazy(() => import('views/Jobs/JobPage'));
const FormBuilderPage = React.lazy(() => import('views/FormBuilder'));
const EquipmentListPage = React.lazy(
  () => import('views/Equipment/EquipmentListPage')
);
const EquipmentCategoryPage = React.lazy(
  () => import('views/Equipment/EquipmentCategoryPage')
);
const CalendarPage = React.lazy(() => import('views/Calendar/CalendarPage'));
const ClientsPage = React.lazy(() => import('views/Clients/ClientsPage'));
const ClientDetailsPage = React.lazy(
  () => import('views/Clients/ClientDetailsPage')
);
const UserProfilePage = React.lazy(
  () => import('views/UserProfile/UserProfilePage')
);
const ForgotPasswordPage = React.lazy(
  () => import('views/Auth/ForgotPassword')
);
const ResetPasswordPage = React.lazy(() => import('views/Auth/ResetPassword'));
const ContactSupportPage = React.lazy(
  () => import('views/ContactSupport/ContactSupportPage')
);

const ReportsPage = React.lazy(() => import('views/Reports/ReportsPage'));
const InboxPage = React.lazy(() => import('views/Inbox/InboxPage'));

const ClientPortalPage = React.lazy(
  () => import('views/ClientPortal/ClientPortalPage')
);

const LimitedUserProfilePage = React.lazy(
  () => import('views/Administration/LimitedUserProfilePage')
);

function withLayout(Layout: any, Component: any) {
  return (
    <Layout>
      <Suspense
        fallback={
          <>
            <h1>[Loading...]</h1>
          </>
        }>
        <Component />
      </Suspense>
    </Layout>
  );
}

interface ProtectedRouteProps extends RouteProps {
  restrictTo?: Role[];
  specialFeatures?: FeatureFlag[];
}

const ProtectedRoute = (props: ProtectedRouteProps) => {
  const { user } = useContext(UserContext);
  const location = useLocation();

  if (!user || user.status === 'error') {
    return (
      <Navigate to="/login" replace state={{ redirect: location.pathname }} />
    );
  }

  if (['loading', 'idle'].includes(user.status)) {
    return <PageLoader />;
  }

  // if (user.status === 'success') {
  //   if (props.specialFeatures) {
  //     if (userHasFullFeatureAccess(user, props.specialFeatures)) {
  //       return props.element;
  //     } else {
  //       return  <div
  //   style={{
  //     display: 'flex',
  //     justifyContent: 'center',
  //     alignItems: 'center',
  //     minHeight: '60vh',
  //   }}>
  //   <AccessDenied user={user} />
  // </div>;
  //     }
  //   }
  // }

  if (user.status === 'success') {
    if (
      props.specialFeatures &&
      userHasFullFeatureAccess(user, props.specialFeatures)
    ) {
      return props.element;
    }
    if (user.isLimitedUser) {
      if (limitedUserHasAccess({ user, route: location.pathname })) {
        return props.element;
      }
    } else if (!props.restrictTo) {
      return props.element;
    } else if (userHasAccess(user, props.restrictTo)) {
      return props.element;
    }

    return (
      <MainLayout>
        <Alert
          severity="error"
          style={{ padding: '2rem', margin: '2rem' }}
          elevation={1}
          variant="outlined">
          <Typography variant="h4" gutterBottom>
            Access Denied!
          </Typography>
          {user.isLimitedUser ? (
            <Typography variant="body1" gutterBottom>
              You does not have access to this page as a Limited User.
            </Typography>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                You do not have the required permissions to proceed to this
                page.
                <br />
                {props.restrictTo?.length > 0 && (
                  <>Only users with the following roles can access this page:</>
                )}
              </Typography>
              {props.restrictTo?.length > 0 &&
                props.restrictTo?.map((role) => (
                  <React.Fragment key={role}>
                    <Typography variant="caption">&bull; {role}</Typography>
                    <br />
                  </React.Fragment>
                ))}
            </>
          )}
          <Typography variant="body2">
            If you do need access, please contact an Administrator from your
            Organization.
          </Typography>
        </Alert>
      </MainLayout>
    );
  }
  return null;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route
        path="/register"
        element={withLayout(MinimalLayout, RegisterPageWrapperWithCaptcha)}
      />
      <Route
        path="/verify-email"
        element={withLayout(MinimalLayout, VerifyEmailScreen)}
      />
      <Route
        path="/verify-email/:token"
        element={withLayout(MinimalLayout, EmailVerificationStatus)}
      />
      <Route path="/login" element={withLayout(MinimalLayout, LoginPage)} />
      <Route
        path="/forgot-password"
        element={withLayout(MinimalLayout, ForgotPasswordPage)}
      />
      <Route
        path="/reset_password/:token"
        element={withLayout(MinimalLayout, ResetPasswordPage)}
      />
      {/*
        Client portal — public on purpose. Report PDFs link here, and the visitor
        is an external client with no login. Access rests on the share token in the
        URL alone — see ADR-0001 in the API repo. Its own shell
        is used instead of MinimalLayout, which hardcodes the Restorize logo.
      */}
      <Route
        path="/portal/:token"
        element={withLayout(React.Fragment, ClientPortalPage)}
      />
      <Route
        path="/portal/:token/:imageKey"
        element={withLayout(React.Fragment, ClientPortalPage)}
      />
      <ProtectedRoute
        path="/"
        element={withLayout(MainLayout, Dashboard)}
        key="index"
      />
      <ProtectedRoute
        path="/administration/*"
        element={withLayout(MainLayout, AdminPage)}
        restrictTo={['Administrator']}
      />
      <ProtectedRoute
        path="/reports/*"
        element={withLayout(MainLayout, ReportsPage)}
        restrictTo={['Administrator', 'ProjectManager', 'Supervisor']}
      />
      <ProtectedRoute
        path="/inbox"
        element={withLayout(MainLayout, InboxPage)}
        restrictTo={['Administrator', 'ProjectManager', 'Supervisor']}
      />
      <ProtectedRoute
        path="/inbox/:tab"
        element={withLayout(MainLayout, InboxPage)}
        restrictTo={['Administrator', 'ProjectManager', 'Supervisor']}
      />
      <ProtectedRoute
        path="/clients"
        element={withLayout(MainLayout, ClientsPage)}
        restrictTo={['Administrator', 'ProjectManager', 'Supervisor']}
      />
      <ProtectedRoute
        path="/clients/:id/*"
        element={withLayout(MainLayout, ClientDetailsPage)}
        restrictTo={['Administrator', 'ProjectManager', 'Supervisor']}
      />
      <ProtectedRoute
        path="/limited-user/:id/*"
        element={withLayout(MainLayout, LimitedUserProfilePage)}
        restrictTo={['Administrator']}
      />
      <ProtectedRoute
        path="/jobs"
        element={withLayout(MainLayout, JobsListPage)}
      />
      <ProtectedRoute
        path="/jobs/:id"
        element={withLayout(MainLayout, JobPage)}
      />
      <ProtectedRoute
        path="/jobs/:id/:tab"
        element={withLayout(MainLayout, JobPage)}
      />
      <ProtectedRoute
        path="/jobs/:id/:tab/:siteVisitID"
        element={withLayout(MainLayout, JobPage)}
      />
      <ProtectedRoute
        path="/equipment"
        element={withLayout(MainLayout, EquipmentListPage)}
        restrictTo={['Administrator']}
      />
      <ProtectedRoute
        path="/equipment/category/:id"
        element={withLayout(MainLayout, EquipmentCategoryPage)}
        restrictTo={['Administrator']}
      />
      <ProtectedRoute
        path="/rate-card/*"
        element={withLayout(MainLayout, RateCard)}
        restrictTo={['Administrator']}
      />
      {/* <ProtectedRoute
        path="/rate-card/consumables"
        element={withLayout(MainLayout, Consumables)}
        restrictTo={['Administrator']}
      /> */}
      <ProtectedRoute
        path="/calendar"
        element={withLayout(MainLayout, CalendarPage)}
      />
      <ProtectedRoute
        path="/users/profile/:id"
        element={withLayout(MainLayout, UserProfilePage)}
      />
      <ProtectedRoute
        path="/formbuilder"
        element={withLayout(MainLayout, FormBuilderPage)}
        restrictTo={['Administrator']}
      />
      <ProtectedRoute
        path="/formbuilder-customize/:jobId"
        element={withLayout(MainLayout, FormBuilderPage)}
        restrictTo={['Administrator', 'ProjectManager', 'Supervisor']}
      />
      <ProtectedRoute
        path="/contact-support"
        element={withLayout(MainLayout, ContactSupportPage)}
      />
    </Routes>
  );
};

export default AppRoutes;
